<template>
  <q-btn
    ref="btn"
    v-bind="btnProps"
    :flat="isHovering ? false : flat"
    :color="isHovering ? hoverColor : color"
    :disable="disable"
    :icon="!(canHover && !isHovering && defaultText) ? icon : undefined"
    :class="{'opaque-70': !hovering && !flat}"
    @click="$emit('click')"
    @mouseenter="onHover"
    @mouseleave="offHover"
  >
    <q-avatar
      v-if="canHover && !isHovering && defaultText"
      size="xs"
      font-size="11px"
    >
      {{ defaultText }}
    </q-avatar>
    <q-tooltip v-if="label">
      {{ label }}
    </q-tooltip>
  </q-btn>
</template>

<script>
/**
 * CancelButton displays an X button for removing an element on the page
 *
 * EVENTS:
 * - click: when button is clicked
 */
export default {
  props: {
    // Label to display when hovering over the button
    label: {
      type: String,
      default: null
    },
    // Color of button
    color: {
      type: String,
      default: 'grey-7'
    },
    // Button color when hovering over
    hoverColor: {
      type: String,
      default: 'grey-7'
    },
    // Disable clicking
    disable: {
      type: Boolean,
      default: false
    },
    // Display as flat button
    flat: {
      type: Boolean,
      default: false
    },
    // Icon to display
    icon: {
      type: String,
      default: 'mdi-close'
    },
    // Text to display inside the button
    // If this is enabled, hover mode is activated so that hovering over the button
    // will swap the "defaultText" with the specified icon^
    defaultText: {
      type: [Number, String],
      default: null
    },
    // If supplied, an action can be performed on a parent element when hovered over this button
    selector: {
      type: String,
      default: null
    },
    // When a selector and defaultText is specified, a class can be supplied to a parent element
    // when the button is hovered over (the class will only display while hovering over this button)
    hoverSelectorClass: {
      type: String,
      default: null
    },
    // Additional QBtn props to bind to this button
    btnProps: {
      type: Object,
      default: () => {
        return {
          'round': true,
          'dense': true,
          'size': 'sm',
          'padding': '1.5px'
        }
      }
    }
  },
  emits: ['click'],
  data () {
    return {
      hovering: false
    }
  },
  computed: {
    canHover () {
      return !!String(this.defaultText)
    },
    isHovering () {
      return this.defaultText ? this.hovering : false
    }
  },
  methods: {
    onHover () {
      this.hovering = true
      if (this.selector && this.hoverSelectorClass) {
        this.$refs.btn.$el.closest(this.selector).classList.add(this.hoverSelectorClass)
      }
    },
    offHover () {
      this.hovering = false
      if (this.selector && this.hoverSelectorClass) {
        this.$refs.btn.$el.closest(this.selector).classList.remove(this.hoverSelectorClass)
      }
    },
    onSelectorHover () {
      this.hovering = true
    },
    offSelectorHover () {
      this.hovering = false
    }
  },
  mounted () {
    if (this.selector && this.canHover && this.defaultText) {
      this.$refs.btn.$el.closest(this.selector).addEventListener('mouseenter', this.onSelectorHover)
      this.$refs.btn.$el.closest(this.selector).addEventListener('mouseleave', this.offSelectorHover)
    }
  },
  beforeUnmount () {
    if (this.selector && this.canHover && this.defaultText) {
      this.$refs.btn.$el.closest(this.selector).removeEventListener('mouseenter', this.onSelectorHover)
      this.$refs.btn.$el.closest(this.selector).removeEventListener('mouseleave', this.offSelectorHover)
    }
  }
}
</script>
